import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { GENERIC_ERROR_MESSAGE, toGozemApiError } from '@shared/utils/http-error.util';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import { GozemMoneyAccountStore } from '../stores/gozem-money-account.store';
import type { CreateTokenRequest, CreateTokenResponse } from '../models/add-token.model';

/**
 * AddTokenApi — HTTP calls for the "Ajouter un compte" inline form.
 *
 * Endpoint:
 *   POST /api/token/create → save a new mobile payment token for a processor
 *
 * Auth credentials are read from localStorage via Storage (same pattern as TopupApi).
 */
@Injectable({
  providedIn: 'root',
})
export class AddTokenApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);
  private readonly gmAccountStore = inject(GozemMoneyAccountStore);

  /**
   * Create a new token (saved payment account) for a processor.
   *
   * @param name             - Display name for the token (token_name on the backend)
   * @param processorId      - Processor._id from the selected TopupMethod
   * @param tokenType        - processor.payment_mode (identifies network / bank type)
   * @param tokenService     - 1 = TOP_UP, 2 = WITHDRAWAL
   * @param tokenProperties  - Dynamic key-value map for processor-specific fields
   *                           (bank_name, account_number, swift_code, etc.)
   * @param intPhone         - Full international phone — only for mobile-money processors
   * @param phone            - Local number only — only for mobile-money processors
   */
  createToken(
    name: string,
    processorId: string,
    tokenType: number,
    tokenService: number,
    tokenProperties: Record<string, string>,
    intPhone?: string,
    phone?: string,
  ): Observable<CreateTokenResponse> {
    const base = this.basePayload();
    const payload: CreateTokenRequest = {
      ...base,
      name,
      processor_id: processorId,
      token_type: tokenType,
      token_service: tokenService,
      token_properties: tokenProperties,
      ...(intPhone !== undefined ? { int_phone: intPhone } : {}),
      ...(phone !== undefined ? { phone } : {}),
    };
    return this.http
      .post<CreateTokenResponse>(`${environment.apiUrl}/api/token/create`, payload)
      .pipe(catchError(this.handleError));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private basePayload(): {
    merchant_id: string;
    account_id: string;
    token: string;
    merchant_user_id: string;
    user_type: 'merchant';
    user_gm_account_id: string;
  } {
    const userData = this.storage.getUserData();
    if (!userData) {
      throw new Error('User data not found in storage');
    }
    return {
      merchant_id: userData.merchantId,
      // Active account, not accounts[0]: pinning to the first row ignores which
      // merchant the user actually selected. See AccountStore.resolveAccountId.
      account_id: this.accountStore.resolveAccountId(),
      token: userData.token,
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      user_gm_account_id: this.gmAccountStore.primaryAccountId(),
    };
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    // Was `?? error.message ?? <fallback>`, which surfaced Angular's own
    // "Http failure response for <url>: 500 OK" text — leaking the internal API URL
    // and status to the user. toGozemApiError never does that, strips the "<code> - "
    // prefix, and keeps the business code so this flow can branch on one later.
    return throwError(() => toGozemApiError(error, GENERIC_ERROR_MESSAGE));
  }
}
